import { Link, createFileRoute } from '@tanstack/react-router';
import { StatusChip } from '@/components/migration/file-drop';
import { EmptyState, PageHeader } from '@/components/page-header';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { fmtAmount } from '@/lib/migration';
import { useOrgId } from '@/lib/org';
import { useImportedTransactions, type ImportedTxnView } from '@/lib/qbo-history';

export const Route = createFileRoute('/settings/migration/history/$runId')({
  component: ImportedTransactions,
});

/** Screen G13 (document 06): what one QuickBooks import created, each linked to the record. */
function ImportedTransactions() {
  const { runId } = Route.useParams();
  const orgId = useOrgId();
  const q = useImportedTransactions(runId);
  const t = useT();
  return (
    <>
      <PageHeader
        title={t('settingsPages.migration.run.title')}
        description={t('settingsPages.migration.run.description')}
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : q.isError ? (
        <EmptyState title={t('settingsPages.migration.run.couldNotLoad')} hint={q.error.message} />
      ) : (
        <Table className="max-w-6xl" data-testid="qbo-imported-transactions">
          <TableHeader>
            <TableRow>
              <TableHead>{t('settingsPages.migration.run.col.date')}</TableHead>
              <TableHead>{t('settingsPages.migration.run.col.quickBooks')}</TableHead>
              <TableHead>{t('settingsPages.migration.run.col.became')}</TableHead>
              <TableHead className="text-end">
                {t('settingsPages.migration.run.col.amount')}
              </TableHead>
              <TableHead>{t('settingsPages.migration.run.col.note')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data.map((x) => (
              <TableRow key={`${x.type}:${x.qboId}`}>
                <TableCell className="tnum">{formatDate(x.date)}</TableCell>
                <TableCell>
                  {x.type} {x.number ?? `#${x.qboId}`}
                </TableCell>
                <TableCell>
                  <Target x={x} />
                </TableCell>
                <TableCell className="tnum text-end">
                  {x.amount !== null ? `${x.currency ?? ''} ${fmtAmount(x.amount)}` : ''}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{x.note ?? ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

function Target({ x }: { x: ImportedTxnView }) {
  const t = useT();
  const label = t(`settingsPages.migration.run.outcome.${x.outcome}`);
  if (x.outcome === 'skipped') return <StatusChip tone="neutral">{label}</StatusChip>;
  const cls = 'underline';
  if (x.documentId) {
    const p = { documentId: x.documentId };
    switch (x.documentKind) {
      case 'invoice':
        return (
          <Link className={cls} to="/sales/invoices/$documentId" params={p}>
            {t('settingsPages.migration.run.kind.invoice')}
          </Link>
        );
      case 'credit_note':
        return (
          <Link className={cls} to="/sales/credit-notes/$documentId" params={p}>
            {t('settingsPages.migration.run.kind.credit_note')}
          </Link>
        );
      case 'sales_receipt':
        return (
          <Link className={cls} to="/sales/sales-receipts/$documentId" params={p}>
            {t('settingsPages.migration.run.kind.sales_receipt')}
          </Link>
        );
      case 'quote':
        return (
          <Link className={cls} to="/sales/quotes/$documentId" params={p}>
            {t('settingsPages.migration.run.kind.quote')}
          </Link>
        );
      case 'bill':
        return (
          <Link className={cls} to="/expenses/bills/$documentId" params={p}>
            {t('settingsPages.migration.run.kind.bill')}
          </Link>
        );
      case 'debit_note':
        return (
          <Link className={cls} to="/expenses/debit-notes/$documentId" params={p}>
            {t('settingsPages.migration.run.kind.debit_note')}
          </Link>
        );
      case 'expense':
        return (
          <Link className={cls} to="/expenses/expenses/$documentId" params={p}>
            {t('settingsPages.migration.run.kind.expense')}
          </Link>
        );
    }
  }
  if (x.paymentId)
    return x.type === 'BillPayment' ? (
      <Link className={cls} to="/expenses/payments/$paymentId" params={{ paymentId: x.paymentId }}>
        {t('settingsPages.migration.run.kind.billPayment')}
      </Link>
    ) : (
      <Link className={cls} to="/sales/payments/$paymentId" params={{ paymentId: x.paymentId }}>
        {t('settingsPages.migration.run.outcome.payment')}
      </Link>
    );
  if (x.journalEntryId)
    return (
      <Link
        className={cls}
        to="/accounting/journal-entries/$entryId"
        params={{ entryId: x.journalEntryId }}
      >
        {t('settingsPages.migration.run.outcome.journal')}
      </Link>
    );
  return <span>{label}</span>;
}
